import { useCallback, useEffect, useState } from "react";
import { CaretDown, CaretUp, FloppyDisk, ListChecks, Plus, Sliders, Trash, X } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { FpShell } from "./FpShell";
import { fpBase, useFpContext } from "@/lib/fp";
import { toast } from "sonner";

/** A plain editable list of names — used for revenue streams and business functions. */
const NameList = ({ items, onChange, placeholder, testid, disabled }) => (
  <div className="space-y-2" data-testid={testid}>
    {(items || []).map((name, i) => (
      <div key={i} className="flex items-center gap-2">
        <input value={name} disabled={disabled}
               onChange={(e) => onChange(items.map((v, j) => (j === i ? e.target.value : v)))}
               data-testid={`${testid}-item-${i}`}
               className="w-full rounded-full border border-border bg-card px-4 py-2 text-sm text-navy outline-none focus:border-terracotta disabled:opacity-60" />
        {!disabled && (
          <button onClick={() => onChange(items.filter((_, j) => j !== i))}
                  data-testid={`${testid}-remove-${i}`}
                  className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-border text-muted-foreground hover:border-destructive hover:text-destructive">
            <Trash size={13} />
          </button>
        )}
      </div>
    ))}
    {!disabled && (
      <button onClick={() => onChange([...(items || []), ""])} data-testid={`${testid}-add`}
              className="inline-flex items-center gap-2 rounded-full border border-dashed border-border px-4 py-2 text-[10px] uppercase tracking-[0.14em] text-navy hover:border-terracotta">
        <Plus size={12} /> {placeholder}
      </button>
    )}
  </div>
);

/** One basis choice — how a variable cost is counted for this client. */
const BasisPicker = ({ label, hint, bases, value, onChange, testid, disabled }) => (
  <div data-testid={testid}>
    <p className="label-caps">{label}</p>
    <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
    <div className="mt-3 grid gap-2 sm:grid-cols-2">
      {(bases || []).map((b) => (
        <button key={b.key} disabled={disabled} onClick={() => onChange(b.key)}
                data-testid={`${testid}-${b.key}`}
                className={`rounded-2xl border p-4 text-left transition-colors disabled:opacity-60 ${
                  value === b.key
                    ? "border-terracotta bg-terracotta/10"
                    : "border-border bg-card hover:border-terracotta/50"}`}>
          <p className="text-sm text-navy">{b.label}</p>
          <p className="mt-1 text-[11px] text-muted-foreground">{b.hint}</p>
        </button>
      ))}
    </div>
  </div>
);

export default function FpSetup() {
  const { tenantId } = useFpContext();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [advanced, setAdvanced] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const { data: out } = await api.get(`${fpBase(tenantId)}/setup`);
      setData(out);
      const s = out.setup;
      const d = s.dimensions || {};
      setForm({
        dimensions: {
          headings: d.headings || [],
          business_functions: d.business_functions || [],
          production_categories: d.production_categories || [],
          sub_channels: d.sub_channels || [],
          channels: d.channels || [],
          components: d.components || [],
        },
        revenue: {
          channels: s.revenue.channels?.length
            ? s.revenue.channels
            : (out.defaults.channels || []).map((n) => ({ name: n, sub_channels: [] })),
          streams: s.revenue.streams?.length ? s.revenue.streams : out.defaults.streams || [],
          totals_source: s.revenue.totals_source || "PL",
          split_source: s.revenue.split_source || "SALES",
        },
        pvc: { basis: s.pvc.basis, attributes: s.pvc.attributes || [],
               object_label: s.pvc.object_label || "",
               wip_mode: s.pvc.wip_mode || "auto",
               wip_ledger: s.pvc.wip_ledger || "Work in Progress" },
        dvc: { basis: s.dvc.basis, attributes: s.dvc.attributes || [] },
        fixed: {
          functions: s.fixed.functions?.length ? s.fixed.functions : out.defaults.functions || [],
          salary_split_source: s.fixed.salary_split_source || "PAYROLL",
          welfare_pool: s.fixed.welfare_pool || "",
          welfare_label: s.fixed.welfare_label || "",
          headcount: s.fixed.headcount || {},
        },
        goals: {
          throughput_fixed_ratio: s.goals?.throughput_fixed_ratio ?? 4,
          overrides: s.goals?.overrides || {},
        },
        simplified_ui: !!s.simplified_ui,
      });
    } catch (e) { setData({ error: errMsg(e) }); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = {
        ...form,
        dimensions: Object.fromEntries(Object.entries(form.dimensions || {})
          .map(([k, v]) => [k, (v || []).map((x) => (x || "").trim()).filter(Boolean)])),
        revenue: {
          ...form.revenue,
          channels: form.revenue.channels
            .filter((c) => (c.name || "").trim())
            .map((c) => ({ name: c.name.trim(),
                           sub_channels: (c.sub_channels || []).filter((s) => (s || "").trim()) })),
          streams: form.revenue.streams.filter((s) => (s || "").trim()),
        },
        fixed: { ...form.fixed, functions: form.fixed.functions.filter((f) => (f || "").trim()),
                 headcount: Object.fromEntries(Object.entries(form.fixed.headcount || {})
                   .filter(([k, v]) => (k || "").trim() && Number(v) > 0)
                   .map(([k, v]) => [k.trim(), Number(v)])) },
        goals: {
          throughput_fixed_ratio: Number(form.goals.throughput_fixed_ratio) || 4,
          overrides: Object.fromEntries(Object.entries(form.goals.overrides || {})
            .filter(([k, v]) => (k || "").trim() && Number(v) > 0)
            .map(([k, v]) => [k.trim(), Number(v)])),
        },
      };
      await api.put(`${fpBase(tenantId)}/setup`, clean);
      toast.success("Configuration saved — the engine now reads this shape");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  const locked = !data?.can_configure;
  const set = (block, patch) => setForm((f) => ({ ...f, [block]: { ...f[block], ...patch } }));
  const objectKeys = data?.cost_object_keys || ["project", "order"];
  const needsAttrs = (area) => objectKeys.includes(form?.[area]?.basis);
  const isCostObject = objectKeys.includes(form?.pvc?.basis);

  return (
    <FpShell title="Configuration" showPeriod={false}
             description="This client's own vocabulary, exactly as its classification master carries it — the headings every ledger is classified under, the business functions, production categories, sub-channels, channels and components. Engine tuning lives under Advanced settings."
             actions={form && !locked && (
               <button onClick={save} disabled={saving} data-testid="fp-setup-save"
                       className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white disabled:opacity-60">
                 <FloppyDisk size={14} /> {saving ? "Saving" : "Save configuration"}
               </button>
             )}>
      {data?.error && <div className="panel p-6 text-sm text-destructive" data-testid="fp-setup-error">{data.error}</div>}
      {!data && <p className="label-caps">Loading setup</p>}

      {form && (
        <div className="space-y-6" data-testid="fp-setup">
          {locked && (
            <div className="panel bg-muted/40 p-4 text-xs text-muted-foreground" data-testid="fp-setup-readonly">
              You can see the setup but not change it — configuration rights are needed to edit.
            </div>
          )}

          {/* ---------------- client vocabulary — the configuration itself ---------------- */}
          <div className="panel p-6" data-testid="fp-setup-dimensions">
            <div className="flex items-start gap-4">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-navy text-white">
                <ListChecks size={18} />
              </div>
              <div>
                <h2 className="font-display text-lg text-navy">Client vocabulary</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  The permitted values from this client's P&amp;L mapping sheet — every ledger is
                  classified through these, and the reconciliation sheets take their rows and
                  columns from here.
                </p>
              </div>
            </div>
            <div className="mt-6 grid gap-8 lg:grid-cols-2">
              {[
                ["headings", "Headings", "The P&L areas — Revenue, PVC, DVC, FC, Net FG COGS", "Add heading"],
                ["business_functions", "Business functions", "Departments the fixed cost is reported by", "Add business function"],
                ["production_categories", "Production categories", "The products production cost is counted by", "Add production category"],
                ["sub_channels", "Sub-channels", "The individual outlets under a channel", "Add sub-channel"],
                ["channels", "Channels", "Outlet, Outreach, Online", "Add channel"],
                ["components", "Components", "The cost components a ledger can carry", "Add component"],
              ].map(([key, label, hint, placeholder]) => (
                <div key={key}>
                  <p className="label-caps">{label}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
                  <div className="mt-3">
                    <NameList items={form.dimensions[key]} disabled={locked}
                              onChange={(v) => set("dimensions", { [key]: v })}
                              placeholder={placeholder} testid={`fp-setup-dim-${key}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ---------------- advanced engine settings ---------------- */}
          <div className="panel p-6" data-testid="fp-setup-advanced">
            <button type="button" onClick={() => setAdvanced((a) => !a)}
                    data-testid="fp-setup-advanced-toggle"
                    className="flex w-full items-center justify-between gap-4 text-left">
              <div>
                <h2 className="font-display text-lg text-navy">Advanced engine settings</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Revenue matrix axes, variable-cost basis, salary split and welfare pooling — the
                  engine's own tuning. The vocabulary above is all most engagements ever touch.
                </p>
              </div>
              {advanced ? <CaretUp size={18} className="shrink-0 text-navy" />
                        : <CaretDown size={18} className="shrink-0 text-navy" />}
            </button>
            {advanced && (
              <div className="mt-6 space-y-6" data-testid="fp-setup-advanced-body">

          {/* ---------------- revenue axes ---------------- */}
          <div className="panel p-6" data-testid="fp-setup-revenue">
            <div className="flex items-start gap-4">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-navy text-white">
                <Sliders size={18} />
              </div>
              <div>
                <h2 className="font-display text-lg text-navy">Revenue matrix</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Delivery channels across, revenue streams down. A channel can hold sub-channels —
                  the individual outlets sit under “Outlets” and roll up to it. The month total
                  comes from the {form.revenue.totals_source}; the split comes from the{" "}
                  {form.revenue.split_source} feed.
                </p>
              </div>
            </div>

            <div className="mt-6 grid gap-8 lg:grid-cols-2">
              <div>
                <p className="label-caps">Delivery channels</p>
                <div className="mt-3 space-y-3">
                  {form.revenue.channels.map((c, i) => (
                    <div key={i} className="rounded-2xl border border-border p-4" data-testid={`fp-setup-channel-${i}`}>
                      <div className="flex items-center gap-2">
                        <input value={c.name} disabled={locked} placeholder="Channel name"
                               onChange={(e) => set("revenue", {
                                 channels: form.revenue.channels.map((v, j) =>
                                   (j === i ? { ...v, name: e.target.value } : v)) })}
                               data-testid={`fp-setup-channel-name-${i}`}
                               className="w-full rounded-full border border-border bg-card px-4 py-2 text-sm text-navy outline-none focus:border-terracotta disabled:opacity-60" />
                        {!locked && (
                          <button onClick={() => set("revenue", {
                            channels: form.revenue.channels.filter((_, j) => j !== i) })}
                                  data-testid={`fp-setup-channel-remove-${i}`}
                                  className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-border text-muted-foreground hover:border-destructive hover:text-destructive">
                            <Trash size={13} />
                          </button>
                        )}
                      </div>
                      <div className="mt-3 pl-4">
                        <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                          Sub-channels within {c.name || "this channel"}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {(c.sub_channels || []).map((s, k) => (
                            <span key={k} className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-[11px] text-navy"
                                  data-testid={`fp-setup-sub-${i}-${k}`}>
                              {s || "—"}
                              {!locked && (
                                <button onClick={() => set("revenue", {
                                  channels: form.revenue.channels.map((v, j) => (j === i
                                    ? { ...v, sub_channels: v.sub_channels.filter((_, m) => m !== k) }
                                    : v)) })}
                                        data-testid={`fp-setup-sub-remove-${i}-${k}`}
                                        className="text-muted-foreground hover:text-destructive">
                                  <X size={11} />
                                </button>
                              )}
                            </span>
                          ))}
                          {!locked && (
                            <input placeholder="add sub-channel + Enter"
                                   onKeyDown={(e) => {
                                     if (e.key !== "Enter" || !e.target.value.trim()) return;
                                     set("revenue", { channels: form.revenue.channels.map((v, j) =>
                                       (j === i ? { ...v, sub_channels: [...(v.sub_channels || []), e.target.value.trim()] } : v)) });
                                     e.target.value = "";
                                   }}
                                   data-testid={`fp-setup-sub-add-${i}`}
                                   className="rounded-full border border-dashed border-border bg-card px-3 py-1 text-[11px] text-navy outline-none focus:border-terracotta" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {!locked && (
                    <button onClick={() => set("revenue", {
                      channels: [...form.revenue.channels, { name: "", sub_channels: [] }] })}
                            data-testid="fp-setup-channel-add"
                            className="inline-flex items-center gap-2 rounded-full border border-dashed border-border px-4 py-2 text-[10px] uppercase tracking-[0.14em] text-navy hover:border-terracotta">
                      <Plus size={12} /> Add delivery channel
                    </button>
                  )}
                </div>
              </div>

              <div>
                <p className="label-caps">Revenue streams</p>
                <div className="mt-3">
                  <NameList items={form.revenue.streams} disabled={locked}
                            onChange={(streams) => set("revenue", { streams })}
                            placeholder="Add revenue stream" testid="fp-setup-streams" />
                </div>
              </div>
            </div>
          </div>

          {/* ---------------- variable cost basis ---------------- */}
          <div className="panel space-y-8 p-6" data-testid="fp-setup-variable">
            <div>
              <h2 className="font-display text-lg text-navy">Variable cost</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                How this client counts variable cost. The Level 2 and Level 3 tables pivot on
                whatever is chosen here, so a project-based or order-based business needs no code
                change — only the column in its feed.
              </p>
            </div>
            <BasisPicker label="Production variable cost is counted by"
                         hint="Sweets and karam for a kitchen; projects for a contractor; orders for a job shop."
                         bases={data.pvc_bases} value={form.pvc.basis} disabled={locked}
                         onChange={(basis) => set("pvc", { basis })} testid="fp-setup-pvc-basis" />
            {needsAttrs("pvc") && (
              <div className="rounded-2xl bg-muted/40 p-4" data-testid="fp-setup-pvc-attrs">
                <p className="label-caps">What describes each {form.pvc.basis}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Recommended: {(data.dimension_attributes[form.pvc.basis] || []).join(" · ")}
                </p>
                <div className="mt-3">
                  <NameList items={form.pvc.attributes.length ? form.pvc.attributes
                                    : data.dimension_attributes[form.pvc.basis] || []}
                            disabled={locked}
                            onChange={(attributes) => set("pvc", { attributes })}
                            placeholder="Add attribute" testid="fp-setup-pvc-attr" />
                </div>
              </div>
            )}
            {isCostObject && (
              <div className="rounded-2xl border border-terracotta/40 bg-terracotta/5 p-5"
                   data-testid="fp-setup-cost-object">
                <p className="label-caps">Cost object · work in progress</p>
                <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
                  A {form.pvc.basis} has a life of its own — it can still be running when the
                  month closes. Name it as this client names it, and tell the engine how an
                  unfinished one is handled. The monthly statements are never altered: they keep
                  reconciling to Tally exactly as filed.
                </p>
                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                      What this client calls it
                    </p>
                    <input value={form.pvc.object_label} disabled={locked}
                           placeholder={`e.g. Work order — blank keeps “${form.pvc.basis}”`}
                           onChange={(e) => set("pvc", { object_label: e.target.value })}
                           data-testid="fp-setup-object-label"
                           className="mt-2 w-full rounded-full border border-border bg-card px-4 py-2 text-sm text-navy outline-none focus:border-terracotta disabled:opacity-60" />
                  </div>
                  {form.pvc.wip_mode === "tally_ledger" && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                        Tally WIP ledger name
                      </p>
                      <input value={form.pvc.wip_ledger} disabled={locked}
                             onChange={(e) => set("pvc", { wip_ledger: e.target.value })}
                             data-testid="fp-setup-wip-ledger"
                             className="mt-2 w-full rounded-full border border-border bg-card px-4 py-2 text-sm text-navy outline-none focus:border-terracotta disabled:opacity-60" />
                    </div>
                  )}
                </div>
                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  {(data.wip_modes || []).map((m) => (
                    <button key={m.key} disabled={locked}
                            onClick={() => set("pvc", { wip_mode: m.key })}
                            data-testid={`fp-setup-wip-mode-${m.key}`}
                            className={`rounded-2xl border p-4 text-left transition-colors disabled:opacity-60 ${
                              form.pvc.wip_mode === m.key
                                ? "border-terracotta bg-terracotta/10"
                                : "border-border bg-card hover:border-terracotta/50"}`}>
                      <p className="text-sm text-navy">{m.label}</p>
                      <p className="mt-1 text-[11px] text-muted-foreground">{m.hint}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <BasisPicker label="Distribution variable cost is counted by"
                         hint="Outlets, online and outreach for a retail chain; projects for a contractor."
                         bases={data.dvc_bases} value={form.dvc.basis} disabled={locked}
                         onChange={(basis) => set("dvc", { basis })} testid="fp-setup-dvc-basis" />
            {needsAttrs("dvc") && (
              <div className="rounded-2xl bg-muted/40 p-4" data-testid="fp-setup-dvc-attrs">
                <p className="label-caps">What describes each {form.dvc.basis}</p>
                <div className="mt-3">
                  <NameList items={form.dvc.attributes.length ? form.dvc.attributes
                                    : data.dimension_attributes[form.dvc.basis] || []}
                            disabled={locked}
                            onChange={(attributes) => set("dvc", { attributes })}
                            placeholder="Add attribute" testid="fp-setup-dvc-attr" />
                </div>
              </div>
            )}
          </div>

          {/* ---------------- fixed cost ---------------- */}
          <div className="panel p-6" data-testid="fp-setup-fixed">
            <h2 className="font-display text-lg text-navy">Fixed cost</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Fixed cost is reported business function wise. Where the P&amp;L carries one salary
              line for the whole company, the split across functions is read from the{" "}
              {form.fixed.salary_split_source} feed.
            </p>
            <div className="mt-4 grid gap-6 lg:grid-cols-2">
              <div>
                <p className="label-caps">Business functions</p>
                <div className="mt-3">
                  <NameList items={form.fixed.functions} disabled={locked}
                            onChange={(functions) => set("fixed", { functions })}
                            placeholder="Add business function" testid="fp-setup-functions" />
                </div>
              </div>
              <div>
                <p className="label-caps">Salary split comes from</p>
                <select value={form.fixed.salary_split_source} disabled={locked}
                        onChange={(e) => set("fixed", { salary_split_source: e.target.value })}
                        data-testid="fp-setup-salary-source"
                        className="mt-3 w-full rounded-full border border-border bg-card px-4 py-2 text-sm text-navy outline-none focus:border-terracotta disabled:opacity-60">
                  {(data.source_options || []).map((s) => (
                    <option key={s.code} value={s.code}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-6 border-t border-border pt-5" data-testid="fp-setup-welfare">
              <p className="label-caps">Staff welfare — spread on headcount</p>
              <p className="mt-1 text-xs text-muted-foreground">
                The books keep canteen, uniform and staff-quarters cost in one group because it is
                spent for everyone. Name that group and declare how many people sit in each
                function — the engine apportions the pool on that headcount and shows each share
                as its own line in the Fixed cost review sheet.
              </p>
              <div className="mt-4 grid gap-6 lg:grid-cols-2">
                <div>
                  <p className="label-caps">Welfare pool (books group)</p>
                  <input value={form.fixed.welfare_pool} disabled={locked}
                         onChange={(e) => set("fixed", { welfare_pool: e.target.value })}
                         placeholder="e.g. Staff Welfare" data-testid="fp-setup-welfare-pool"
                         className="mt-3 w-full rounded-full border border-border bg-card px-4 py-2 text-sm text-navy outline-none focus:border-terracotta disabled:opacity-60" />
                  <p className="label-caps mt-4">Shown in the model as</p>
                  <input value={form.fixed.welfare_label} disabled={locked}
                         onChange={(e) => set("fixed", { welfare_label: e.target.value })}
                         placeholder="Employee welfare — apportioned"
                         data-testid="fp-setup-welfare-label"
                         className="mt-3 w-full rounded-full border border-border bg-card px-4 py-2 text-sm text-navy outline-none focus:border-terracotta disabled:opacity-60" />
                </div>
                <div>
                  <p className="label-caps">Headcount per function</p>
                  <div className="mt-3 space-y-2" data-testid="fp-setup-headcount">
                    {(form.fixed.functions || []).filter((f) => (f || "").trim()).map((fn) => (
                      <div key={fn} className="flex items-center gap-3">
                        <p className="w-40 truncate text-sm text-navy">{fn}</p>
                        <input type="number" min="0" step="1" disabled={locked}
                               value={form.fixed.headcount?.[fn] ?? ""}
                               onChange={(e) => set("fixed", { headcount: { ...(form.fixed.headcount || {}), [fn]: e.target.value } })}
                               data-testid={`fp-setup-headcount-${fn}`}
                               className="w-24 rounded-full border border-border bg-card px-4 py-2 text-center text-sm text-navy outline-none focus:border-terracotta disabled:opacity-60" />
                        <p className="text-xs text-muted-foreground">people</p>
                      </div>
                    ))}
                    {!(form.fixed.functions || []).some((f) => (f || "").trim()) && (
                      <p className="text-xs text-muted-foreground">
                        Add business functions above first — headcount is declared per function.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
              </div>
            )}
          </div>

          {data.setup?.version > 0 && (
            <p className="text-[11px] text-muted-foreground" data-testid="fp-setup-version">
              Version {data.setup.version}
              {data.setup.updated_by_email ? ` · last saved by ${data.setup.updated_by_email}` : ""}
            </p>
          )}
        </div>
      )}
    </FpShell>
  );
}
